import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
const CASE_TYPES = ["Criminal Case", "Civil Case", "Cheque Bounce", "Enforcement Case"];
const PRIORITIES = ["LOW", "MEDIUM", "HIGH", "CRITICAL"];
export function CaseCreate() {
    const navigate = useNavigate();
    const [companies, setCompanies] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [legalFirms, setLegalFirms] = useState([]);
    const [users, setUsers] = useState([]);
    const [companyId, setCompanyId] = useState("");
    const [customerId, setCustomerId] = useState("");
    const [legalFirmId, setLegalFirmId] = useState("");
    const [caseDate, setCaseDate] = useState(new Date().toISOString().slice(0, 10));
    const [caseType, setCaseType] = useState(CASE_TYPES[0]);
    const [caseAgainst, setCaseAgainst] = useState("");
    const [authorizedSignatory, setAuthorizedSignatory] = useState("");
    const [receivableAmount, setReceivableAmount] = useState("");
    const [chequeAmount, setChequeAmount] = useState("");
    const [priority, setPriority] = useState("MEDIUM");
    const [responsibleUserIds, setResponsibleUserIds] = useState([]);
    const [fieldErrors, setFieldErrors] = useState({});
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);
    useEffect(() => {
        api.get("/companies").then((r) => setCompanies(r.items));
        api.get("/customers").then((r) => setCustomers(r.items));
        api.get("/legal-firms").then((r) => setLegalFirms(r.items.filter((f) => f.isActive)));
        api.get("/users").then((r) => setUsers(r.items));
    }, []);
    function validate() {
        const errs = {};
        if (!companyId)
            errs.companyId = "Select a company.";
        if (!customerId)
            errs.customerId = "Select a customer.";
        if (!caseDate)
            errs.caseDate = "Case date is required.";
        if (!receivableAmount || Number(receivableAmount) <= 0)
            errs.receivableAmount = "Enter a receivable amount greater than zero.";
        if (responsibleUserIds.length === 0)
            errs.responsibleUserIds = "Assign at least one responsible person.";
        setFieldErrors(errs);
        return Object.keys(errs).length === 0;
    }
    async function submit() {
        if (!validate())
            return;
        setSaving(true);
        setError(null);
        try {
            const created = await api.post("/cases", {
                companyId,
                customerId,
                legalFirmId: legalFirmId || undefined,
                caseDate: new Date(caseDate).toISOString(),
                caseType,
                caseAgainst: caseAgainst.trim() || undefined,
                authorizedSignatory: authorizedSignatory.trim() || undefined,
                receivableAmount: Number(receivableAmount),
                chequeAmount: chequeAmount ? Number(chequeAmount) : undefined,
                priority,
                responsibleUserIds,
            });
            navigate(`/legal-cases/cases/${created.id}`);
        }
        catch (e) {
            setError(e?.code === "forbidden" ? "You don't have permission to create cases." : "Couldn't create this case.");
        }
        finally {
            setSaving(false);
        }
    }
    return (<div>
      <h1 style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: "1.6rem", margin: "0 0 18px" }}>New Case</h1>

      {error && <div style={{ color: "var(--st-risk)", marginBottom: 12 }}>{error}</div>}

      <div style={{ background: "var(--surface)", border: "1px solid var(--line-soft)", borderRadius: 10, padding: 22, maxWidth: 720 }}>
        <div className="lg-stack" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <Field label="Company *" error={fieldErrors.companyId}>
            <select value={companyId} onChange={(e) => setCompanyId(e.target.value)} style={inputStyle}>
              <option value="">Select company…</option>
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>

          <Field label="Customer *" error={fieldErrors.customerId} hint={<Link to="/legal-cases/customers" style={hintLink}>+ Add new customer</Link>}>
            <select value={customerId} onChange={(e) => setCustomerId(e.target.value)} style={inputStyle}>
              <option value="">Select customer…</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>

          <Field label="Legal Firm" hint={<Link to="/legal-cases/legal-firms" style={hintLink}>+ Add new firm</Link>}>
            <select value={legalFirmId} onChange={(e) => setLegalFirmId(e.target.value)} style={inputStyle}>
              <option value="">Not yet assigned</option>
              {legalFirms.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
          </Field>

          <Field label="Case Type">
            <select value={caseType} onChange={(e) => setCaseType(e.target.value)} style={inputStyle}>
              {CASE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </Field>

          <Field label="Case Date *" error={fieldErrors.caseDate}>
            <input type="date" value={caseDate} onChange={(e) => setCaseDate(e.target.value)} style={inputStyle}/>
          </Field>

          <Field label="Priority">
            <select value={priority} onChange={(e) => setPriority(e.target.value)} style={inputStyle}>
              {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </Field>

          <Field label="Receivable Amount (QAR) *" error={fieldErrors.receivableAmount}>
            <input type="number" min="0" step="0.01" value={receivableAmount} onChange={(e) => setReceivableAmount(e.target.value)} style={inputStyle}/>
          </Field>

          <Field label="Cheque Amount (QAR)">
            <input type="number" min="0" step="0.01" value={chequeAmount} onChange={(e) => setChequeAmount(e.target.value)} style={inputStyle}/>
          </Field>

          <Field label="Responsible Person(s) *" error={fieldErrors.responsibleUserIds}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 14px", border: "1px solid var(--line)", borderRadius: 8, padding: "9px 10px" }}>
              {users.map((u) => (<label key={u.id} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: "0.82rem", cursor: "pointer" }}>
                  <input type="checkbox" checked={responsibleUserIds.includes(u.id)} onChange={(e) => setResponsibleUserIds((ids) => (e.target.checked ? [...ids, u.id] : ids.filter((id) => id !== u.id)))}/>
                  {u.name}
                </label>))}
            </div>
          </Field>

          <Field label="Case Against / Authorized Signatory">
            <input placeholder="Authorized signatory name" value={authorizedSignatory} onChange={(e) => setAuthorizedSignatory(e.target.value)} style={inputStyle}/>
          </Field>

          <Field label="Notes">
            <input placeholder="Case against (optional)" value={caseAgainst} onChange={(e) => setCaseAgainst(e.target.value)} style={inputStyle}/>
          </Field>
        </div>

        <button onClick={submit} disabled={saving} style={{ ...btnStyle, marginTop: 20 }}>
          {saving ? "Creating…" : "Create Case"}
        </button>
      </div>
    </div>);
}
function Field({ label, error, hint, children }) {
    return (<div>
      <label style={{ display: "flex", justifyContent: "space-between", fontSize: "0.78rem", fontWeight: 600, color: "var(--ink-soft)", marginBottom: 5 }}>
        <span>{label}</span>
        {hint}
      </label>
      {children}
      {error && <div style={{ color: "var(--st-risk)", fontSize: "0.72rem", marginTop: 3 }}>{error}</div>}
    </div>);
}
const inputStyle = { border: "1px solid var(--line)", borderRadius: 8, padding: "8px 10px", fontSize: "0.82rem", fontFamily: "inherit", width: "100%" };
const btnStyle = { background: "var(--brand)", color: "#fff", fontWeight: 600, fontSize: "0.85rem", padding: "10px 20px", borderRadius: 8, border: "none", cursor: "pointer" };
const hintLink = { fontSize: "0.72rem", fontWeight: 500, color: "var(--accent-ink)" };
